// @flow

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';
import * as R from 'ramda';
import AssignTaskDialog from './AssignTasksDialog';
import SPBackend from '../../services/SPBackend';
import useInterval from '../../hooks/useInterval';
import { log } from '../../utils/jsUtils';
import MemberDrawer from '../MemberDrawer/index';
import RequestHeader from './RequestHeader';
import RequestContent from './RequestContent';
import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../utils/stylesUtils';
import styles from './Styles/MemberHome.Style';
import { Box } from '@mui/material';
import {
  formatPhoneNumberForBackend,
  validateEmail
} from '../../utils/jsUtils';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { updateRequests } from '../../reduxStore/modules/feed/actions';
import { selectUser, selectUserState } from '../../reduxStore/modules/user/selectors';
import { selectIsDemoMode } from '../../reduxStore/modules/common/selectors';
import { useI18n } from '../../contexts/I18nContext';
import { useAssignStaffToTasksMutation } from '../../features/api/staffApi';
import type { Tenant } from '../../services/SPBackend';

type Props = {
  classes: Object
};

const RequestHome = (props: Props) => {
  const { classes } = props;
  let { requestId } = useParams();
  log('[RequestHome] requestId: ', requestId);

  const theme = useTheme();
  const dispatch = useDispatch();
  const styledClasses = createClasses(styles, theme);

  // drawer state & controls
  const [mobileOpen, setMobileOpen] = useState(false);
  const [requestHistory, setRequestHistory] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [isV2Format, setIsV2Format] = useState(null);
  const [request, setRequest] = useState(null);
  const [loadingRequest, setLoadingRequest] = useState(true);
  const [unit, setUnit] = useState(null);
  const [openAssignTaskDialog, setOpenAssignTaskDialog] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const usehandleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const updateTaskCallback = (task) => (newTask) => {
    if (newTask !== null) {
      log('[updateTaskCallback] newTask: ', newTask);
      setRequest(newTask);
      setTenant(newTask.lookupTenant);
    }
    refreshRequestDetails();
  };

  // Use Redux selectors instead of direct store access
  const user = useSelector(selectUser);
  const userState = useSelector(selectUserState);
  const isDemoMode = useSelector(selectIsDemoMode);
  const i18n = useI18n();
  const [assignStaffToTasksMutation] = useAssignStaffToTasksMutation();

  const refreshRequestDetails = (successCallback) => {
    if (user !== null) {
      log('[refreshRequestDetails]...');
      setLoadingRequest(true);
      SPBackend.getRequestHistory(requestId, user)
      .then(response => {
        log('[getRequestHistory] data: ', response);
        const { task, unit, history, tenant, isV2Format } = response.data;
        setIsV2Format(isV2Format);
        setTenant(tenant);
        setRequest(task);
        setUnit(unit);

        // Add request to Redux so UPDATE_REQUESTS dispatches from TagCustomersPicker work
        // This enables optimistic tag updates on /member/xxx to match /member behavior
        if (task) {
          dispatch(updateRequests([{
            ...task,
            lookupTenant: tenant
          }]));
        }

        const byDate = R.descend(R.prop('Date'));
        if (typeof history !== 'undefined' && history !== null) {
          const sortedHistory = R.sort(byDate, history);
          setRequestHistory(sortedHistory);
        }
        setLoadingRequest(false);
        if (typeof successCallback !== 'undefined' 
          && typeof successCallback === 'function') {
          successCallback();
        }
      })
      .catch(error => {
        log('[getRequestHistory] error', error);
        setLoadingRequest(false); 
      });
    }
  };

  useEffect(() => {
    log('[useEffect] refreshing request details...');
    refreshRequestDetails();
  }, [requestId, user]);

  const updateRequestStatus = (newStatus, callbackSetLoading) => () => {
    callbackSetLoading(prevState => !prevState);
    if (request !== null) {
      SPBackend.updateRequest(request._id, newStatus, user)
        .then(response => {
          const { data : updatedRequest } = response;
          log('[updated request]: ', updatedRequest);
          setRequest(updatedRequest);
          dispatch(setSnackMessage({
            message: `${i18n.t('task')} updated.`,
            level: 'success',
            duration: 5000
          }));
          callbackSetLoading(prevState => !prevState);
          refreshRequestDetails();
        })
        .catch(err => {
          log('err: ', err);
        });
    }
  };

  const handleAssignTask = (tasks, assignedStaff) => {
    if (!request || !assignedStaff) return;

    log('[handleAssignTask] assignedStaff: ', assignedStaff);

    // Create the request object for optimistic update
    const existingRequests = [{
      ...request,
      lookupTenant: tenant
    }];

    // Optimistic update: update local state immediately for detail view
    setRequest(prev => ({
      ...prev,
      Assignee: assignedStaff
    }));

    // Call RTK Query mutation (handles optimistic feed update and notifications)
    assignStaffToTasksMutation({
      taskIds: tasks.map(t => t._id),
      assigneeId: assignedStaff._id,
      assignee: assignedStaff,
      existingRequests
    });
  };

  // look for unread messages within this task
  if (process.env.REACT_APP_FEED_AUTOREFRESH === 'true') {
    useInterval(() => {
      if (requestId
        && typeof user !== 'undefined'
        && user !== null) {
        log('[RequestHome] refreshing unread count...');
        SPBackend.getTaskUnreadCount(requestId, user)
        .then((response) => {
          log('[getTaskUnreadCount] response: ', response);
          setUnreadCount(response.data.UnreadCount);
        })
        .catch((error) => {
          log('[getTaskUnreadCount] error: ', error);
        });
      }
      log('[RequestHome] focusing on last element...');
      
    }, 5000);
  }

  return (
    <Box sx={styledClasses.root}>
      <Helmet>
        <title>Neo | Conversation</title>
      </Helmet>
      <RequestHeader
        isDemoMode={isDemoMode}
        mobileOpen={mobileOpen} 
        refreshRequestDetails={refreshRequestDetails}
        request={request}
        setOpenAssignTaskDialog={setOpenAssignTaskDialog}
        unit={unit}
        updateRequestStatus={updateRequestStatus}
        usehandleDrawerToggle={usehandleDrawerToggle}
      />
      <MemberDrawer 
        mobileOpen={mobileOpen} 
        usehandleDrawerToggle={usehandleDrawerToggle}
      />
      <RequestContent
        isV2Format={isV2Format}
        loadingRequest={loadingRequest}
        refreshRequestDetails={refreshRequestDetails}
        request={request}
        requestHistory={requestHistory}
        setTenant={setTenant}
        tenant={tenant}
        unit={unit}
        unreadCount={unreadCount}
        updateTaskCallback={updateTaskCallback}
      /> 
      <AssignTaskDialog
        open={openAssignTaskDialog}
        setOpen={setOpenAssignTaskDialog}
        tasks={[request]}
        triggerAction={handleAssignTask}
      />
    </Box>
  );
};

export default RequestHome;